import type { ClientLibraryId } from 'types/client-library'
import type { Thunk } from 'types/redux'
import { mapResponseToReducerObjects } from '@disco/state-management/actions/PlaylistTrackActions'
import api from '@disco/api/server'
import {
  CLIENT_LIBRARY_FEATURED_PLAYLISTS_SET,
  CLIENT_LIBRARY_FEATURED_PLAYLISTS_SET_FAIL,
  CLIENT_LIBRARY_FEATURED_PLAYLISTS_SET_SUCCESS,
  CLIENT_LIBRARY_FEATURED_PLAYLISTS_UPDATE,
  CLIENT_LIBRARY_FEATURED_PLAYLISTS_UPDATE_FAIL,
  CLIENT_LIBRARY_FEATURED_PLAYLISTS_UPDATE_SUCCESS,
  CLIENT_LIBRARY_PLAYLISTS_LOAD,
  CLIENT_LIBRARY_PLAYLISTS_LOAD_FAIL,
  CLIENT_LIBRARY_PLAYLISTS_LOAD_NEXT,
  CLIENT_LIBRARY_PLAYLISTS_LOAD_NEXT_FAIL,
  CLIENT_LIBRARY_PLAYLISTS_LOAD_NEXT_SUCCESS,
  CLIENT_LIBRARY_PLAYLISTS_LOAD_SUCCESS,
  CLIENT_LIBRARY_PLAYLISTS_UPDATE,
  CLIENT_LIBRARY_PLAYLISTS_UPDATE_FAIL,
  CLIENT_LIBRARY_PLAYLISTS_UPDATE_SUCCESS,
} from '@disco/constants/ActionTypes'
import { ALBUMS } from '@disco/constants/FeatureTypes'
import { isFeatureEnabled } from '@disco/state-management/selectors/business'
import { omit } from '@disco/utils/array'
import { pushUINotification } from '@disco/utils/notifications'
/**
 * Loads playlists for a client library with optional search and featured filtering.
 * @param libraryId - ID of the client library to load playlists for
 * @param query - Search query to filter playlists
 * @param isFeatured - Optional flag to filter only featured playlists
 * @returns Thunk that loads playlists based on albums feature availability
 */
export function clientLibraryPlaylistsLoad(
  libraryId: ClientLibraryId,
  query: string,
  isFeatured?: boolean,
): Thunk {
  return async (dispatch, getState) => {
    const albumsEnabled = isFeatureEnabled(getState(), {
      featureName: ALBUMS,
    })
    dispatch({
      type: CLIENT_LIBRARY_PLAYLISTS_LOAD,
      libraryId,
    })

    try {
      const response = await api.getClientLibraryPlaylists(
        libraryId,
        albumsEnabled ? 'playlist' : undefined,
        query,
        isFeatured,
      )
      mapResponseToReducerObjects(dispatch, getState, {
        ...response,
        results: response.results.map(item => omit(item, ['is_featured'])),
      })
      dispatch({
        type: CLIENT_LIBRARY_PLAYLISTS_LOAD_SUCCESS,
        libraryId,
        response,
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_PLAYLISTS_LOAD_FAIL,
        libraryId,
        error,
      })
    }
  }
}
/**
 * Loads the next page of playlists for a client library using pagination.
 * @param libraryId - ID of the client library to load next playlists for
 * @param url - Pagination URL for the next page of playlists
 * @returns Thunk that loads next page of playlists
 */
export function clientLibraryPlaylistsLoadNext(
  libraryId: ClientLibraryId,
  url: string,
): Thunk {
  return async (dispatch, getState) => {
    dispatch({
      type: CLIENT_LIBRARY_PLAYLISTS_LOAD_NEXT,
      libraryId,
    })

    try {
      const response = await api.getUrl<any>(
        url,
        null,
        'api.getClientLibraryPlaylistsLoadNext',
      )
      mapResponseToReducerObjects(dispatch, getState, {
        ...response,
        results: response.results.map(item => omit(item, ['is_featured'])),
      })
      dispatch({
        type: CLIENT_LIBRARY_PLAYLISTS_LOAD_NEXT_SUCCESS,
        libraryId,
        response,
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_PLAYLISTS_LOAD_NEXT_FAIL,
        libraryId,
        error,
      })
    }
  }
}
/**
 * Updates playlists in a client library by adding and removing specified playlists.
 * @param libraryId - ID of the client library to update
 * @param to_add - Array of playlist IDs to add to the library
 * @param to_remove - Array of playlist IDs to remove from the library
 * @returns Thunk that updates playlists and shows success notification
 */
export function clientLibraryPlaylistsUpdate(
  libraryId: ClientLibraryId,
  to_add: number[],
  to_remove: number[],
): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_PLAYLISTS_UPDATE,
      libraryId,
      payload: {
        to_add,
        to_remove,
      },
    })

    try {
      await api.alterClientLibraryPlaylists(libraryId, to_add, to_remove)
      dispatch({
        type: CLIENT_LIBRARY_PLAYLISTS_UPDATE_SUCCESS,
        libraryId,
      })
      pushUINotification({
        message: 'Playlists updated!',
        level: 'success',
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_PLAYLISTS_UPDATE_FAIL,
        libraryId,
        error,
      })
    }
  }
}
/**
 * Updates featured playlists in a client library by adding and removing specified playlists.
 * @param libraryId - ID of the client library to update featured playlists for
 * @param to_add - Array of playlist IDs to add to featured playlists
 * @param to_remove - Array of playlist IDs to remove from featured playlists
 * @returns Thunk that updates featured playlists and shows success notification
 */
export function clientLibraryFeaturedPlaylistsUpdate(
  libraryId: ClientLibraryId,
  to_add: number[],
  to_remove: number[],
): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_FEATURED_PLAYLISTS_UPDATE,
      libraryId,
      payload: {
        to_add,
        to_remove,
      },
    })

    try {
      await api.alterClientLibraryFeaturedPlaylists(
        libraryId,
        to_add,
        to_remove,
      )
      dispatch({
        type: CLIENT_LIBRARY_FEATURED_PLAYLISTS_UPDATE_SUCCESS,
        libraryId,
      })
      pushUINotification({
        message: 'Playlists updated!',
        level: 'success',
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_FEATURED_PLAYLISTS_UPDATE_FAIL,
        libraryId,
        error,
      })
    }
  }
}
/**
 * Sets the complete list of featured playlists for a client library.
 * @param libraryId - ID of the client library to set featured playlists for
 * @param playlistIds - Array of playlist IDs to set as featured playlists
 * @returns Thunk that sets featured playlists and shows success notification
 */
export function clientLibraryFeaturedPlaylistsSet(
  libraryId: ClientLibraryId,
  playlistIds: number[],
): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_FEATURED_PLAYLISTS_SET,
      libraryId,
      playlistIds,
    })

    try {
      await api.setClientLibraryFeaturedPlaylists(libraryId, {
        playlist_ids: playlistIds,
      })
      dispatch({
        type: CLIENT_LIBRARY_FEATURED_PLAYLISTS_SET_SUCCESS,
        libraryId,
      })
      pushUINotification({
        message: 'Playlists updated!',
        level: 'success',
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_FEATURED_PLAYLISTS_SET_FAIL,
        libraryId,
        error,
      })
    }
  }
}
